/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { AccessType } from "@quintype/components";
import { connect } from "react-redux";
import loadingIcon from "../../../assets/icons/loading.svg";
import { SvgHandler } from "../svg-handler/svg-handler";
import { accessCheck } from "../../../isomorphic/utils";
import "./subscription-page.m.css";
import LoaderOverlay from "../molecules/loader-overlay";

class SubscriptionTemplate extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedSubscriptions: {},
      isSubscribeButtonClicked: false,
      selectedPlan: {}
    };
  }

  async handleSubscriptionClick(e, subscriptionGroup) {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isSubscribeButtonClicked: true });
    const firstPlan = get(subscriptionGroup, ["subscription_plans", 0]);
    const selectedPlan = get(this.state, ["selectedSubscriptions", subscriptionGroup.id], firstPlan);
    this.setState({ selectedPlan });

    if (!this.props.member) {
      this.setState({ isWaitingForUserToLogin: true });
      this.props.openLoginTemplate();
      return false;
    }
  }

  openPaymentScreen(plan = {}) {
    const paymentGateway = get(plan, ["supported_payment_providers", 0], "razorpay");

    if (paymentGateway === "razorpay") {
      return this.props.initRazorPayPayment(plan, "standard");
    }
    if (paymentGateway === "paypal") {
      const options = {
        selectedPlan: plan,
        planType: "standard",
        returnUrl: `${document.location.origin}/paypal-return-url`,
        cancelUrl: `${document.location.origin}/paypal-cancel-url`
      };
      return this.props.initPaypalPayment(options);
    }
  }

  navigateToUserDetailsPage() {
    this.props.navigateTo(`/user-details`);
  }

  async componentDidUpdate(prevProps) {
    if (prevProps.member !== this.props.member) {
      this.setState({ isWaitingForUserToLogin: false });
    }
    if (
      this.props.member &&
      this.state.isSubscribeButtonClicked &&
      this.state.selectedPlan &&
      this.state.isAccessTypeUserSet &&
      !this.state.isWaitingForPayment &&
      !this.state.isWaitingForUserToLogin
    ) {
      this.setState({
        isWaitingForPayment: true
      });
      try {
        const subscription = await this.openPaymentScreen(this.state.selectedPlan);
        subscription && this.navigateToUserDetailsPage();
      } finally {
        this.setState({
          isWaitingForPayment: false,
          isSubscribeButtonClicked: false,
          selectedPlan: {}
        });
      }
    }
    if (this.props.member && !this.state.isAccessTypeUserSet && !this.state.isAccessTypeUserSetLoading) {
      this.setState({
        isAccessTypeUserSetLoading: true
      });
      // Bad implementation. Currently AccessType component not exposing a helper
      // fn to call setUser with the jwtToken.
      // Added issue to fix these issues: https://github.com/quintype/quintype-node-components/issues/145
      this.props.initAccessType(() => {
        this.setState({
          isAccessTypeUserSet: true,
          isAccessTypeUserSetLoading: false
        });
      });
    }
  }

  getSubscriptionBlocks(subscriptionGroups) {
    return subscriptionGroups.map((subscriptionGroup, index) => (
      <div styleName="group" key={`group-${index}`}>
        <div styleName="desc-wrapper">
          <span styleName="subscription-group">{subscriptionGroup.name}</span>
          <div styleName="wrapper-content">
            <p styleName="plan-time">
              {this.getCurrencyType(subscriptionGroups, index)}
              &nbsp;
              {this.getPriceBlock(subscriptionGroups, index)}
              <span styleName="month">{this.getPlanDuration(subscriptionGroups, index)}</span>
            </p>
            <p styleName="plan-desc">{subscriptionGroup.description}</p>
            {this.getDurationBlock(subscriptionGroup)}
          </div>
        </div>
      </div>
    ));
  }

  getPriceBlock(subscriptionGroups, currentGroupIndex) {
    const firstPlanPrice = get(subscriptionGroups, [currentGroupIndex, "subscription_plans", 0, "price_cents"]);
    return (
      get(
        this.state,
        ["selectedSubscriptions", subscriptionGroups[currentGroupIndex].id, "price_cents"],
        firstPlanPrice
      ) / 100
    );
  }

  getCurrencyType(subscriptionGroups, currentGroupIndex) {
    const firstPlanCurrencyType = get(subscriptionGroups, [
      currentGroupIndex,
      "subscription_plans",
      0,
      "price_currency"
    ]);
    return get(
      this.state,
      ["selectedSubscriptions", subscriptionGroups[currentGroupIndex].id, "price_currency"],
      firstPlanCurrencyType
    );
  }
  getPlanDuration(subscriptionGroups, currentGroupIndex) {
    const firstPlanUnit = get(subscriptionGroups, [currentGroupIndex, "subscription_plans", 0, "duration_unit"]);
    const firstPlanDuration = get(subscriptionGroups, [currentGroupIndex, "subscription_plans", 0, "duration_length"]);
    const planUnit = get(
      this.state,
      ["selectedSubscriptions", subscriptionGroups[currentGroupIndex].id, "duration_unit"],
      firstPlanUnit
    );
    const planDuration = get(
      this.state,
      ["selectedSubscriptions", subscriptionGroups[currentGroupIndex].id, "duration_length"],
      firstPlanDuration
    );

    return (
      <span>
        <span>{planDuration}</span>
        <span>{planUnit}</span>
      </span>
    );
  }

  getDurationBlock(subscriptionGroup) {
    const plans = get(subscriptionGroup, ["subscription_plans"], []);
    const subscriptionGroupId = get(subscriptionGroup, ["id"]);
    const planList = (
      <div styleName="select-wrapper">
        <select onChange={e => this.handlePlanChange(e, subscriptionGroupId)} styleName="plan-selection">
          {plans.map((plan, index) => (
            <option key={`plan-${index}`} value={JSON.stringify(plan)}>
              {plan.title}
            </option>
          ))}
        </select>
      </div>
    );
    return (
      <div styleName="duration-wrapper">
        {planList}
        <button
          data-testid="plans-subscribe-btn"
          styleName="subs-btn"
          onClick={e => this.handleSubscriptionClick(e, subscriptionGroup)}
        >
          Subscribe
        </button>
      </div>
    );
  }

  handlePlanChange(e, subscriptionGroupId) {
    const currentPlan = JSON.parse(e.target.value);
    const currentState = Object.assign({}, this.state.selectedSubscriptions, {
      [subscriptionGroupId]: currentPlan
    });
    this.setState({ selectedSubscriptions: currentState });
  }

  render() {
    const themeAttribute = get(this.props, ["config", "theme-attributes"]);

    const PbAccessTypeConfig = get(this.props, ["config", "pagebuilder-config", "general", "accesstypeIntegration"]);
    const planLayout = get(PbAccessTypeConfig, ["planPageLayout"], get(themeAttribute, ["plan-page-layout"], ""));
    const planTitle = get(PbAccessTypeConfig, ["planPageTitle"], get(themeAttribute, ["plan_page_title"], ""));
    const planDesc = get(PbAccessTypeConfig, ["planPageDescription"], get(themeAttribute, ["plan_page_desc"], ""));

    const groups = get(this.props, ["subscriptions"], []);

    return (
      <>
        {this.state.isAccessTypeUserSetLoading ? <LoaderOverlay /> : null}
        <div className="component-wrapper" styleName={`${planLayout === "list-view" ? "component-wrapper" : " "}`}>
          <div styleName="content-wrapper">
            <h1 styleName="plan-title">{planTitle}</h1>
            <p>{planDesc}</p>
          </div>
          {groups && groups.length ? (
            <div styleName={`${planLayout === "list-view" ? "list-view" : "groups-layout"}`}>
              {this.getSubscriptionBlocks(groups)}
            </div>
          ) : (
            <SvgHandler clazzName="loader" xlinkHref={loadingIcon} />
          )}
        </div>
      </>
    );
  }
}

SubscriptionTemplate.propTypes = {
  initAccessType: PropTypes.func,
  initRazorPayPayment: PropTypes.func,
  initPaypalPayment: PropTypes.func,
  openLoginTemplate: PropTypes.func,
  getSubscriptionForUser: PropTypes.func,
  navigateTo: PropTypes.func,
  member: PropTypes.object,
  getSubscription: PropTypes.func
};

function SubscriptionBase(props) {
  const email = get(props, ["member", "email"]);
  const phone = get(props, ["member", "metadata", "phone-number"], "1234");
  const themeAttributes = get(props, ["config", "theme-attributes"]);
  const PbAccessTypeConfig = get(props, ["config", "pagebuilder-config", "general", "accesstypeIntegration"]);
  const accessTypeKey = get(PbAccessTypeConfig, ["accesstypeKey"], get(themeAttributes, ["accesstypeKey"]));
  const accessTypeBkIntegrationId = get(
    PbAccessTypeConfig,
    ["bridgeKeeperIntegrationId"],
    get(themeAttributes, ["accesstypeBkIntegrationId"], "")
  );

  const isStaging =
    get(PbAccessTypeConfig, ["accesstypeMode"]) === "staging" ||
    get(themeAttributes, ["accesstypeEnvironment"]) === "staging";

  const enableAccesstype = get(
    PbAccessTypeConfig,
    ["enableAccesstype"],
    get(themeAttributes, ["enableAccesstype"], false)
  );

  const storyId = get(props, ["story", "id"]);
  return (
    <AccessType
      enableAccesstype={accessCheck(enableAccesstype)}
      isStaging={isStaging}
      accessTypeKey={accessTypeKey}
      accessTypeBkIntegrationId={accessTypeBkIntegrationId}
      email={email}
      phone={phone}
      storyId={storyId}
    >
      {({ initAccessType, initRazorPayPayment, initPaypalPayment }) => (
        <SubscriptionTemplate
          initPaypalPayment={initPaypalPayment}
          initRazorPayPayment={initRazorPayPayment}
          initAccessType={initAccessType}
          {...props}
        />
      )}
    </AccessType>
  );
}

const mapStateToProps = state => ({
  config: get(state, ["qt", "config"], {}),
  subscriptions: get(state, ["subscriptions"], null),
  member: get(state, ["member"], null)
});

const mapDispatchToProps = dispatch => ({
  openLoginTemplate: () => dispatch({ type: "OPEN_LOGIN_TEMPLATE" }),
  navigateTo: url => global.app.navigateToPage(dispatch, url)
});

export const Subscription = connect(
  mapStateToProps,
  mapDispatchToProps
)(SubscriptionBase);
